/**
 * @file useSiteMenus.ts
 * @description Меню шапки и подвала в черновике (phase19.16 Р1; SB15, SB16,
 * SB81; контракт §2): загрузка, добавление, правка, удаление, перестановка.
 *
 * Каждая правка несёт версию части MENUS; 409 — «Меню изменил другой
 * сотрудник» и перечитывание. После правки список перечитывается с сервера
 * (у пункта меняются цель, статус и замечания — их считает сервер),
 * перестановка показывается сразу и откатывается при ошибке. Ошибки меню
 * подвала (SB113: пункт вне колонки, больше 3 колонок) — своим текстом.
 *
 * «SEO-техника» v0.19.65 (SB146): у части MENUS есть ещё документ навигации
 * («Показывать хлебные крошки», `useNavigationDraft`) с той же версией части;
 * его запись поднимает версию в черновике каркаса — меню берёт её оттуда,
 * чтобы следующая правка пункта не получила ложный 409.
 */

import type { SiteMenuLocation } from "@chaiberi/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import { useSiteView } from "../../components/site-view/site-view-context";
import { useNotify } from "../../components/ui/NotifyProvider";
import { siteView } from "../../lib/api-site-view";
import { parseVersionConflict, siteViewErrorText } from "../../lib/site-view/errors";
import { footerMenuIssueText } from "../../lib/site-view/footer-columns";
import { toOrder } from "../../lib/site-view/menu-tree";
import type { SiteMenuItem, SiteMenuItemInput, SiteMenusResponse } from "../../lib/site-view/types";

export interface SiteMenusApi {
  menus: SiteMenusResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  create: (location: SiteMenuLocation, parentId: string | null, fields: SiteMenuItemInput) => Promise<boolean>;
  update: (id: string, patch: Partial<SiteMenuItemInput>) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  reorder: (location: SiteMenuLocation, tree: SiteMenuItem[]) => Promise<void>;
}

export function useSiteMenus(): SiteMenusApi {
  const view = useSiteView();
  const notify = useNotify();
  const [menus, setMenus] = useState<SiteMenusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const versionRef = useRef(0);

  const accept = useCallback(
    (next: SiteMenusResponse) => {
      versionRef.current = next.part.version;
      setMenus(next);
      view.applyPart(next.part);
    },
    [view],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const next = await siteView.menus();
      versionRef.current = next.part.version;
      setMenus(next);
      setError(null);
    } catch (err) {
      setError(siteViewErrorText(err, "Не удалось загрузить меню. Сайт работает как прежде."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Версию части мог поднять документ навигации (вкладка «Крошки») — берём большую.
  const draftVersion = view.draft?.parts.MENUS?.version;
  useEffect(() => {
    if (draftVersion !== undefined && draftVersion > versionRef.current) versionRef.current = draftVersion;
  }, [draftVersion]);

  /** Общая обработка правки: успех — перечитать, 409 — объяснить и перечитать. */
  const run = useCallback(
    async (action: (version: number) => Promise<unknown>, failText: string): Promise<boolean> => {
      try {
        await action(versionRef.current);
        accept(await siteView.menus());
        return true;
      } catch (err) {
        const conflict = parseVersionConflict(err);
        if (conflict) {
          notify.error(`Меню изменил ${conflict.editedBy?.name ?? "другой сотрудник"} — показываем его версию, повторите правку.`);
          await reload();
        } else {
          notify.error(footerMenuIssueText(err) ?? siteViewErrorText(err, failText));
        }
        return false;
      }
    },
    [accept, notify, reload],
  );

  const create = useCallback<SiteMenusApi["create"]>(
    (location, parentId, fields) =>
      run((v) => siteView.createMenuItem({ expectedVersion: v, location, parentId, ...fields }), "Не удалось добавить пункт."),
    [run],
  );
  const update = useCallback<SiteMenusApi["update"]>(
    (id, patch) => run((v) => siteView.updateMenuItem(id, { expectedVersion: v, ...patch }), "Не удалось сохранить пункт."),
    [run],
  );
  const remove = useCallback<SiteMenusApi["remove"]>(
    (id) => run((v) => siteView.deleteMenuItem(id, v), "Не удалось удалить пункт."),
    [run],
  );

  const reorder = useCallback<SiteMenusApi["reorder"]>(
    async (location, tree) => {
      if (!menus) return;
      const previous = menus;
      setMenus({ ...menus, [location]: tree });
      try {
        accept(await siteView.reorderMenu({ expectedVersion: versionRef.current, location, items: toOrder(tree) }));
      } catch (err) {
        setMenus(previous);
        const conflict = parseVersionConflict(err);
        notify.error(
          conflict
            ? `Меню изменил ${conflict.editedBy?.name ?? "другой сотрудник"} — порядок не сохранён, показываем его версию.`
            : (footerMenuIssueText(err) ?? siteViewErrorText(err, "Не удалось сохранить порядок пунктов.")),
        );
        if (conflict) await reload();
      }
    },
    [accept, menus, notify, reload],
  );

  return { menus, loading, error, reload, create, update, remove, reorder };
}
